/**
 * BadmintonStatsCard
 *
 * Gamified badminton career statistics card (singles & doubles).
 * Reuses volleyball vb-* CSS classes for visual consistency.
 *
 * Props:
 *   stats  — object returned by teamService.getPlayerCareerStats()
 *   user   — { name, username, ... } from AuthContext
 */
import React from 'react';
import '../../../styles/components/sportStats.css';

const fmt = (n, decimals = 0) => (n == null ? '—' : Number(n).toFixed(decimals));
const perGame = (total, matches, dec = 2) =>
  matches > 0 ? fmt(total / matches, dec) : '—';
const perGameNum = (total, matches) => (matches > 0 ? total / matches : null);

const formatDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return null; }
};

const LEVELS = [
  { min: 0,  max: 4,  label: 'ROOKIE',   color: '#94a3b8', next: 5  },
  { min: 5,  max: 14, label: 'AMATEUR',  color: '#4ade80', next: 15 },
  { min: 15, max: 29, label: 'PRO',      color: '#38bdf8', next: 30 },
  { min: 30, max: 49, label: 'ELITE',    color: '#a78bfa', next: 50 },
  { min: 50, max: Infinity, label: 'LEGEND', color: '#fbbf24', next: null },
];
const getLevel = (matches) => {
  const lvl = LEVELS.find((l) => matches >= l.min && matches <= l.max) || LEVELS[0];
  const progress = lvl.next
    ? Math.min(100, ((matches - lvl.min) / (lvl.next - lvl.min)) * 100)
    : 100;
  return { ...lvl, progress };
};

// Winner distribution donut — includes XC winners in legend
const WinnerDonut = ({ smashWinners = 0, dropWinners = 0, netKills = 0, crossCourt = 0 }) => {
  const total = Math.max(smashWinners + dropWinners + netKills + crossCourt, 1);
  const r = 44; const cx = 56; const cy = 56;
  const circ = 2 * Math.PI * r;
  const smashPct = smashWinners / total;
  const dominanceScore = ((smashWinners / total) * 100).toFixed(1);
  const ringColor = parseFloat(dominanceScore) >= 50 ? '#f59e0b' : '#38bdf8';

  return (
    <div className="vb-donut-wrapper">
      <div className="vb-donut-ring-wrap">
        <svg viewBox="0 0 112 112" className="vb-donut-svg">
          <defs>
            <filter id="bdRingGlow">
              <feGaussianBlur stdDeviation="3" result="blur"/>
              <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
            </filter>
          </defs>
          <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="13"/>
          <circle cx={cx} cy={cy} r={r} fill="none"
            stroke={ringColor} strokeWidth="13"
            strokeDasharray={`${circ * smashPct} ${circ * (1 - smashPct)}`}
            strokeDashoffset={circ * 0.25}
            strokeLinecap="round"
            filter="url(#bdRingGlow)"/>
          <text x={cx} y={cy - 5} textAnchor="middle" className="vb-donut-main">{dominanceScore}</text>
          <text x={cx} y={cy + 12} textAnchor="middle" className="vb-donut-sub">SMASH%</text>
        </svg>
      </div>
      <div className="vb-donut-legend">
        <div className="vb-donut-row">
          <span className="vb-dot" style={{background: ringColor}}/>
          <span className="vb-donut-text">SMASH: <strong className="vb-donut-val">{smashWinners}</strong></span>
        </div>
        <div className="vb-donut-row">
          <span className="vb-dot" style={{background:'rgba(56,189,248,0.6)'}}/>
          <span className="vb-donut-text">DROP: <strong className="vb-donut-val">{dropWinners}</strong></span>
        </div>
        <div className="vb-donut-row">
          <span className="vb-dot" style={{background:'rgba(72,187,120,0.6)'}}/>
          <span className="vb-donut-text">NET: <strong className="vb-donut-val">{netKills}</strong></span>
        </div>
        <div className="vb-donut-row">
          <span className="vb-dot" style={{background:'rgba(167,139,250,0.6)'}}/>
          <span className="vb-donut-text">XC: <strong className="vb-donut-val">{crossCourt}</strong></span>
        </div>
      </div>
    </div>
  );
};

const StatSpot = ({ icon, label, value }) => (
  <div className="vb-spot">
    <div className="vb-spot__icon">{icon}</div>
    <div className="vb-spot__value">{value}</div>
    <div className="vb-spot__label">{label}</div>
  </div>
);

const TrophyCard = ({ icon, label, value, detail, rank }) => {
  const colors = { gold: '#fbbf24', silver: '#94a3b8', bronze: '#cd7f32' };
  const c = colors[rank] || 'rgba(255,255,255,0.2)';
  return (
    <div className="vb-trophy" style={{'--trophy-color': c}}>
      <div className="vb-trophy__icon">{icon}</div>
      <div className="vb-trophy__body">
        <div className="vb-trophy__label">{label}</div>
        <div className="vb-trophy__value">{value ?? '—'}</div>
        {detail && <div className="vb-trophy__detail">{detail}</div>}
      </div>
      {value != null && <div className="vb-trophy__badge">PB</div>}
    </div>
  );
};

const BarRow = ({ label, value, rawNum, maxRef }) => {
  const pct = rawNum != null && maxRef > 0 ? Math.min(100, (rawNum / maxRef) * 100) : 0;
  return (
    <div className="vb-bar-row">
      <div className="vb-bar-header">
        <span className="vb-bar-label">{label}</span>
        <span className="vb-bar-val">{value}</span>
      </div>
      <div className="vb-bar-track">
        <div className="vb-bar-fill" style={{width: `${pct}%`}}/>
      </div>
    </div>
  );
};

/**
 * MatchChip — recent match summary.
 * Display priority: match_name → short_code → match DB id as last resort.
 */
const MatchChip = ({ match }) => {
  const smash = match.metrics?.smashWinner       || 0;
  const drops = match.metrics?.dropWinner        || 0;
  const xc    = match.metrics?.crossCourtWinner  || 0;
  const aces  = match.metrics?.serviceAce        || 0;
  const date  = formatDate(match.matchDate);

  const displayTitle = match.matchTitle
    || (match.matchShortCode ? `#${match.matchShortCode}` : null)
    || `Match ${match.matchId}`;

  const resultIcon = match.result === 'win' ? '🏆' : match.result === 'loss' ? '❌' : match.result === 'draw' ? '➖' : null;

  return (
    <div className="vb-match-chip">
      <div className="vb-match-chip__header">
        <div className="vb-match-chip__title">{displayTitle}</div>
        {resultIcon && <span className="vb-match-chip__result">{resultIcon}</span>}
      </div>
      <div className="vb-match-chip__date">{date || '—'}</div>
      <div className="vb-match-chip__stats">
        <span className="vb-chip-stat"><span className="vb-chip-ico">⚡</span>{smash}</span>
        <span className="vb-chip-stat"><span className="vb-chip-ico">🪶</span>{drops}</span>
        {xc > 0 && <span className="vb-chip-stat"><span className="vb-chip-ico">↗️</span>{xc}</span>}
        <span className="vb-chip-stat"><span className="vb-chip-ico">🏸</span>{aces}</span>
        {match.scoreContribution > 0 && (
          <span className="vb-chip-stat vb-chip-pts"><span className="vb-chip-ico">📊</span>{match.scoreContribution}</span>
        )}
      </div>
    </div>
  );
};

// ── Main ──────────────────────────────────────────────────────────────────────
const BadmintonStatsCard = ({ stats = {}, user = {}, sportProfile = {} }) => {
  const {
    careerTotals      = {},
    careerHighs       = {},
    matchesPlayed      = 0,
    winsCount          = 0,
    lossesCount        = 0,
    drawsCount         = 0,
    scoreContribution  = 0,
    recentMatches      = [],
  } = stats;

  // ── Metric aggregations ─────────────────────────────────────────────────
  const smashWinners  = careerTotals.smashWinner       || 0;
  const dropWinners   = careerTotals.dropWinner        || 0;
  const netKills      = careerTotals.netKill           || 0;
  const crossCourt    = careerTotals.crossCourtWinner  || 0;
  const serviceAces   = careerTotals.serviceAce        || 0;

  // UE events = all discipline/fault actions recorded: error + foul + sideOut + unforcedError
  const ueEvents = (careerTotals.error         || 0)
                 + (careerTotals.foul          || 0)
                 + (careerTotals.sideOut       || 0)
                 + (careerTotals.unforcedError || 0);

  const totalWinners   = smashWinners + dropWinners + netKills + crossCourt + serviceAces;

  // UE rate displayed as per-match average
  const ueRateDisplay = matchesPlayed > 0
    ? `${(ueEvents / matchesPlayed).toFixed(1)}/m`
    : '—';

  const level    = getLevel(matchesPlayed);
  const hasStats = matchesPlayed > 0;

  const swps = perGameNum(smashWinners, matchesPlayed);
  const dwps = perGameNum(dropWinners,  matchesPlayed);
  const nkps = perGameNum(netKills,     matchesPlayed);
  const ccps = perGameNum(crossCourt,   matchesPlayed);
  const saps = perGameNum(serviceAces,  matchesPlayed);
  const maxBar = Math.max(swps||0, dwps||0, nkps||0, ccps||0, saps||0, 1);

  const high = (key) => {
    const h = careerHighs[key];
    if (!h) return { value: null, detail: null };
    return {
      value:  h.value,
      detail: h.matchDate ? `vs. ${h.matchTitle || h.matchShortCode || 'match'} · ${formatDate(h.matchDate)}` : null,
    };
  };

  const displayName = user?.first_name
    ? `${user.first_name} ${user.last_name || ''}`.trim().toUpperCase()
    : (user?.username || 'PLAYER').toUpperCase();

  const winRate = matchesPlayed > 0
    ? `${Math.round((winsCount / matchesPlayed) * 100)}%`
    : '—';

  return (
    <div className="vb-card">
      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <div className="vb-hero">
        <div className="vb-hero__bg-orbs" aria-hidden="true">
          <span className="vb-orb vb-orb--1"/>
          <span className="vb-orb vb-orb--2"/>
        </div>
        <div className="vb-hero__ball" aria-hidden="true">🏸</div>
        <div className="vb-hero__info">
          <div className="vb-hero__name">{displayName}</div>
          <div className="vb-hero__sport">Badminton · Career Statistics</div>
          {(sportProfile?.position || sportProfile?.playingStyle) && (
            <div className="vb-hero__profile">
              {sportProfile.position     && <span className="vb-hero__position">📍 {sportProfile.position}</span>}
              {sportProfile.playingStyle && <span className="vb-hero__style">🎮 {sportProfile.playingStyle}</span>}
            </div>
          )}
        </div>
        <div className="vb-hero__meta">
          <div className="vb-level-badge" style={{'--lv-color': level.color}}>
            <span className="vb-level-badge__label">LEVEL</span>
            <span className="vb-level-badge__name">{level.label}</span>
          </div>
          <div className="vb-hero__matches">
            <span className="vb-hero__matches-num">{matchesPlayed}</span>
            <span className="vb-hero__matches-label">{' '}Match{matchesPlayed !== 1 ? 'es' : ''} Played</span>
          </div>
          {(winsCount > 0 || lossesCount > 0) && (
            <div className="vb-hero__record">
              <span className="vb-hero__win">🏆 {winsCount}W</span>
              <span className="vb-hero__sep"> · </span>
              <span className="vb-hero__loss">❌ {lossesCount}L</span>
              {drawsCount > 0 && <><span className="vb-hero__sep"> · </span><span className="vb-hero__draw">➖ {drawsCount}D</span></>}
              <span className="vb-hero__sep"> · </span>
              <span className="vb-hero__winrate">{winRate}</span>
            </div>
          )}
        </div>
        <div className="vb-xp-bar-wrap">
          <div className="vb-xp-bar-track">
            <div className="vb-xp-bar-fill" style={{width: `${level.progress}%`}}/>
          </div>
          <span className="vb-xp-label">
            {level.next ? `${matchesPlayed} / ${level.next} matches to ${LEVELS.find(l => l.min === level.next)?.label || 'MAX'}` : '🏆 MAX LEVEL'}
          </span>
        </div>
      </div>

      {/* ── CORE STAT SPOTLIGHTS ─────────────────────────────────────────── */}
      <div className="vb-spots-row">
        <StatSpot icon="⚡" label="SMASHES"    value={hasStats ? smashWinners  : '—'}/>
        <StatSpot icon="🪶" label="DROPS"      value={hasStats ? dropWinners   : '—'}/>
        <StatSpot icon="🏹" label="NET KILLS"  value={hasStats ? netKills      : '—'}/>
        <StatSpot icon="↗️" label="XC WINNERS" value={hasStats ? crossCourt    : '—'}/>
        <StatSpot icon="🏸" label="ACES"       value={hasStats ? serviceAces   : '—'}/>
        <StatSpot icon="📊" label="UE RATE"    value={hasStats ? ueRateDisplay : '—'}/>
      </div>

      <div className="vb-card__body">
        {/* LEFT col */}
        <div className="vb-card__left">
          <h4 className="vb-section-title">🥇 CAREER HIGHS</h4>
          <div className="vb-trophies">
            <TrophyCard icon="⚡" label="SMASH WINNERS"  rank="gold"   {...high('smashWinner')}/>
            <TrophyCard icon="🪶" label="DROP WINNERS"   rank="silver" {...high('dropWinner')}/>
            <TrophyCard icon="🏹" label="NET KILLS"      rank="bronze" {...high('netKill')}/>
            <TrophyCard icon="↗️" label="XC WINNERS"     rank="gold"   {...high('crossCourtWinner')}/>
            <TrophyCard icon="🏸" label="SERVICE ACES"   rank="silver" {...high('serviceAce')}/>
            <TrophyCard
              icon="⚠️"
              label="UNFORCED ERR"
              rank="bronze"
              value={ueEvents > 0 ? ueEvents : null}
              detail={matchesPlayed > 0 ? `${(ueEvents / matchesPlayed).toFixed(1)} per match` : null}
            />
          </div>
        </div>

        {/* RIGHT col */}
        <div className="vb-card__right">
          <h4 className="vb-section-title">📊 WINNER DISTRIBUTION</h4>
          <WinnerDonut
            smashWinners={smashWinners}
            dropWinners={dropWinners}
            netKills={netKills}
            crossCourt={crossCourt}
          />

          <h4 className="vb-section-title vb-section-title--mt">📈 PER-MATCH AVERAGES</h4>
          <div className="vb-bars">
            <BarRow label="Smash W / Match"  value={perGame(smashWinners, matchesPlayed)} rawNum={swps} maxRef={maxBar}/>
            <BarRow label="Drop W / Match"   value={perGame(dropWinners,  matchesPlayed)} rawNum={dwps} maxRef={maxBar}/>
            <BarRow label="Net K / Match"    value={perGame(netKills,     matchesPlayed)} rawNum={nkps} maxRef={maxBar}/>
            <BarRow label="XC W / Match"     value={perGame(crossCourt,   matchesPlayed)} rawNum={ccps} maxRef={maxBar}/>
            <BarRow label="Aces / Match"     value={perGame(serviceAces,  matchesPlayed)} rawNum={saps} maxRef={maxBar}/>
          </div>

          <div className="vb-totals-row">
            <div className="vb-total-chip">
              <span className="vb-total-val">{fmt(totalWinners)}</span>
              <span className="vb-total-lbl">Winners</span>
            </div>
            <div className="vb-total-chip">
              <span className="vb-total-val">{fmt(ueEvents)}</span>
              <span className="vb-total-lbl">Faults</span>
            </div>
            <div className="vb-total-chip">
              <span className="vb-total-val">{fmt(scoreContribution)}</span>
              <span className="vb-total-lbl">Pts Scored</span>
            </div>
          </div>
        </div>
      </div>

      {recentMatches.length > 0 && (
        <div className="vb-recent">
          <h4 className="vb-section-title vb-section-title--center">🕹️ RECENT MATCHES</h4>
          <div className="vb-match-list">
            {recentMatches.slice(0, 5).map((m) => (
              <MatchChip key={m.matchId} match={m}/>
            ))}
          </div>
        </div>
      )}

      {!hasStats && (
        <div className="vb-empty">
          <div className="vb-empty__ball">🏸</div>
          <div className="vb-empty__heading">YOUR JOURNEY BEGINS HERE</div>
          <p className="vb-empty__sub">No advanced scoring stats recorded yet.</p>
          <p className="vb-empty__cta">
            Play a live match in <strong>Advanced Scoring</strong> mode to start
            unlocking your badminton career statistics.
          </p>
          <div className="vb-empty__unlock-chips">
            <span className="vb-unlock-chip">⚡ Track Smashes</span>
            <span className="vb-unlock-chip">🪶 Track Drops</span>
            <span className="vb-unlock-chip">🏹 Track Net Kills</span>
            <span className="vb-unlock-chip">📊 Winner Distribution</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default BadmintonStatsCard;
